/**
 * Masters → Banking, Document series, and Items.
 * @file src/web/features/admin/components/masters/RecordsTabs.tsx
 *
 * Three small masters that share one shape: list, add through an inline form,
 * delete. Grouped in one file because separating them would mean three copies
 * of the same twenty lines.
 *
 * Each empty state explains why the table is empty for a new client, because
 * for all three that is the normal starting condition — Tally supplies none of
 * them, and "no rows" on its own reads like a failed load.
 */

import { useEffect, useState } from 'react';
import { Trash2, Star, Landmark, FileStack, Package, Upload, Loader2, Plus, X, Search, Pencil, Check } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetchJson } from '../../lib/admin-api';
import { usePeriod } from '../../lib/period-context';
import {
  Card, Field, TextInput, Select, PrimaryBtn, GhostBtn, AddToggle, FormError,
  Loading, ErrBox, EmptyState, Th, Td, Pill, useEntityResource, mutate, useDeleteConfirm,
} from './shared';

/* ══════════════════════════════════════════════════════════════════════
   Banking
   ══════════════════════════════════════════════════════════════════════ */

interface BankAccount {
  id: string; accountName: string | null; accountNumber: string; ifsc: string;
  bankName: string | null; branch: string | null; accountType: string | null;
  isPrimary: boolean; usedForChallans: boolean;
}

export function BankingTab({ entityId }: { entityId: string | null }) {
  const { items, loading, error, reload } = useEntityResource<BankAccount>('bank-accounts', entityId);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ accountName: '', accountNumber: '', ifsc: '', bankName: '', branch: '', accountType: 'current' });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));
  const { askDelete, deleteDialog } = useDeleteConfirm();

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate('bank-accounts', 'POST', { entityId, ...f });
      setAdding(false);
      setF({ accountName: '', accountNumber: '', ifsc: '', bankName: '', branch: '', accountType: 'current' });
      toast.success('Bank account added'); reload();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  const act = async (fn: () => Promise<void>, msg: string) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error((e as Error).message); }
  };

  const summary = loading ? 'loading…'
    : error ? 'could not load'
    : items.length === 0 ? '— none on file'
    : `· ${items.length} ${items.length === 1 ? 'account' : 'accounts'}`;

  return (
    <Card
      collapsible
      defaultOpen={false}
      summary={summary}
      title="Bank details"
      subtitle="Used for challan generation (TDS, PF, ESI, PT, GST) and bank-statement reconciliation."
      action={!loading && !error
        ? <AddToggle open={adding} onToggle={() => setAdding(!adding)} label="Add account" />
        : undefined}
    >
      {loading && <Loading label="Loading bank accounts" />}
      {error && <div className="px-6 py-5"><ErrBox msg={error} /></div>}

      {!loading && !error && <>
      {adding && (
        <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
          <FormError msg={err} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Bank name"><TextInput value={f.bankName} onChange={set('bankName')} placeholder="HDFC Bank" /></Field>
            <Field label="Account number"><TextInput value={f.accountNumber} onChange={set('accountNumber')} mono /></Field>
            <Field label="IFSC" hint="11 characters — this is what routes a challan payment.">
              <TextInput value={f.ifsc} onChange={(v) => set('ifsc')(v.toUpperCase())} placeholder="HDFC0001234" mono />
            </Field>
            <Field label="Account name"><TextInput value={f.accountName} onChange={set('accountName')} /></Field>
            <Field label="Branch"><TextInput value={f.branch} onChange={set('branch')} /></Field>
            <Field label="Type">
              <Select value={f.accountType} onChange={set('accountType')} options={['current', 'savings', 'od', 'cc']} />
            </Field>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <PrimaryBtn onClick={submit} busy={busy}>Add account</PrimaryBtn>
            <button onClick={() => setAdding(false)} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
          </div>
        </div>
      )}

      {items.length === 0 && !adding ? (
        <EmptyState what="bank account" why="Needed before a challan can be generated for any statutory payment, and to reconcile a bank statement against the books." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr><Th>Bank</Th><Th>Account</Th><Th>IFSC</Th><Th>Branch</Th><Th>Type</Th><Th right>Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {items.map((b) => (
                <tr key={b.id} className="hover:bg-ink-50/50">
                  <Td>
                    <span className="inline-flex items-center gap-2">
                      <Landmark className="h-3.5 w-3.5 text-ink-400" />
                      {b.bankName ?? '—'}
                      {b.isPrimary && <Pill tone="brand"><Star className="h-3 w-3" /> Primary</Pill>}
                      {b.usedForChallans && <Pill tone="ok">Challans</Pill>}
                    </span>
                  </Td>
                  <Td mono>{b.accountNumber}</Td>
                  <Td mono>{b.ifsc}</Td>
                  <Td muted>{b.branch ?? '—'}</Td>
                  <Td muted>{b.accountType ?? '—'}</Td>
                  <Td right>
                    <span className="inline-flex gap-1.5">
                      {!b.isPrimary && (
                        <GhostBtn onClick={() => act(() => mutate(`bank-accounts/${b.id}`, 'PATCH', { entityId, isPrimary: true }), 'Primary account changed')}>
                          <Star className="h-3 w-3" /> Make primary
                        </GhostBtn>
                      )}
                      <GhostBtn
                        tone="danger"
                        onClick={() => askDelete(
                          `${b.bankName ?? 'bank account'} ${b.accountNumber}`,
                          () => act(() => mutate(`bank-accounts/${b.id}`, 'DELETE', { entityId }), 'Account removed'),
                        )}
                      >
                        <Trash2 className="h-3 w-3" />
                      </GhostBtn>
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>}
      {deleteDialog}
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Document series — GSTR-1 Table 13
   ══════════════════════════════════════════════════════════════════════ */

interface DocumentSeries {
  id: string; name: string; prefix: string | null; suffix: string | null; natureCode: number;
  voucherType: string | null; active: boolean;
  startNumber: number | null; restartEveryFy: boolean; numberWidth: number | null;
  /** Whether new documents made in Oddits take their number from this series. */
  autoNumbers?: boolean;
  /** Where the numbering stands in the financial year on screen. */
  usage?: SeriesStanding;
}
/** Where a series stands against the numbers in the books — for a saved series
 *  and, from the pre-save check, for one still being typed. */
interface SeriesStanding {
  status: 'ok' | 'new' | 'mismatch';
  count: number; first: string | null; last: string | null;
  /** Null when the series must not issue a number. */
  next: string | null;
  fromTally: number; fromOddits: number; mixed: boolean;
  warning: string | null; overlapWarning: string | null; startHint: string | null;
  preview?: string;
}
/** A series the books' own document numbers imply, offered to pre-fill the form. */
interface SeriesSuggestion { voucherType: string; prefix: string; suffix: string; count: number; example: string; natureCode: number; inPeriod?: number; startNumber?: number; numberWidth?: number | null }
interface DocNature { code: number; label: string }

export function SeriesTab({ entityId }: { entityId: string | null }) {
  const period = usePeriod();
  const fy = period.startYear != null ? { from: `${period.startYear}-04-01`, to: `${period.startYear + 1}-03-31`, label: `${period.startYear}-${String((period.startYear + 1) % 100).padStart(2, '0')}` } : null;
  // With the financial year on screen, so each series shows where its
  // numbering stands in THAT year.
  const { items, loading, error, reload } = useEntityResource<DocumentSeries>(`document-series${fy ? `?from=${fy.from}&to=${fy.to}` : ''}`, entityId);
  const [natures, setNatures] = useState<DocNature[]>([]);
  const [adding, setAdding] = useState(false);
  // autoNumber: null = not stated, so it follows the voucher type — on, except for purchases.
  const BLANK = { name: '', startNumber: '1', prefix: '', suffix: '', numberWidth: '', restartEveryFy: true, autoNumber: null as boolean | null, natureCode: '1', voucherType: '' };
  const [f, setF] = useState(BLANK);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: 'name' | 'startNumber' | 'prefix' | 'suffix' | 'numberWidth' | 'natureCode' | 'voucherType') => (v: string) => setF((s) => ({ ...s, [k]: v }));

  /* What the document numbers already in the books say the series are. Typing
     "SIH/" and "/26-27" by hand is how a series ends up one character off and
     matching nothing; the numbers themselves know. Reloaded with the list, so
     a suggestion disappears once a series covers it. */
  const [suggestions, setSuggestions] = useState<SeriesSuggestion[]>([]);
  useEffect(() => {
    if (!entityId || loading) return;
    // With the financial year in the top bar, so the year being worked on is
    // looked at first and its series listed first.
    adminFetchJson<{ items: SeriesSuggestion[] }>('/api/masters/document-series/suggestions', { query: { entityId, from: fy?.from, to: fy?.to } })
      .then((d) => setSuggestions(d.items ?? []))
      .catch(() => setSuggestions([]));
  }, [entityId, loading, items.length, fy?.from, fy?.to]);
  /* Checked against the books as it is typed, before it is saved: a prefix
     missing its "/" gives "SIH126-27" beside a book of "SIH/166/26-27", and the
     time to see that is now, not on the first invoice. Read-only on the
     server; a short pause so it does not fire on every keystroke. */
  const [check, setCheck] = useState<SeriesStanding | null>(null);
  useEffect(() => {
    if (!adding || !entityId || !/^\d+$/.test(f.startNumber.trim())) { setCheck(null); return; }
    let cancel = false;
    const t = window.setTimeout(() => {
      adminFetchJson<SeriesStanding>(`/api/masters/document-series/check${fy ? `?from=${fy.from}&to=${fy.to}` : ''}`, {
        method: 'POST',
        body: JSON.stringify({
          entityId, name: f.name, prefix: f.prefix, suffix: f.suffix, voucherType: f.voucherType,
          startNumber: Number(f.startNumber), numberWidth: f.numberWidth.trim() ? Number(f.numberWidth) : null,
          restartEveryFy: f.restartEveryFy, autoNumber: f.autoNumber,
        }),
      }).then((d) => !cancel && setCheck(d)).catch(() => !cancel && setCheck(null));
    }, 350);
    return () => { cancel = true; window.clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adding, entityId, f.name, f.prefix, f.suffix, f.voucherType, f.startNumber, f.numberWidth, f.restartEveryFy, f.autoNumber, fy?.from]);

  const fillFrom = (s: SeriesSuggestion) => {
    // The starting number comes with it: the lowest number found for this
    // series in the year on screen. Shown in the form, and editable there.
    setF({
      name: s.voucherType, startNumber: String(s.startNumber ?? 1), prefix: s.prefix, suffix: s.suffix,
      numberWidth: s.numberWidth ? String(s.numberWidth) : '', restartEveryFy: true, autoNumber: null,
      natureCode: String(s.natureCode), voucherType: s.voucherType,
    });
    setErr(null); setAdding(true);
  };

  useEffect(() => {
    adminFetchJson<{ docNatures: DocNature[] }>('/api/masters/meta')
      .then((d) => setNatures(d.docNatures ?? []))
      .catch(() => setNatures([]));
  }, []);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      if (!/^\d+$/.test(f.startNumber.trim())) throw new Error('Starting number is required — the number this series begins with, e.g. 1');
      await mutate('document-series', 'POST', {
        entityId, ...f, natureCode: Number(f.natureCode),
        startNumber: Number(f.startNumber), numberWidth: f.numberWidth.trim() ? Number(f.numberWidth) : null,
      });
      setAdding(false); setF(BLANK);
      toast.success('Series added'); reload();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  /* The starting number can be corrected in place. It is the one field of a
     series a person is expected to change after the fact — a pre-filled value
     that was wrong, or a run that should begin elsewhere next April. */
  const [editing, setEditing] = useState<{ id: string; value: string } | null>(null);
  const saveStart = async () => {
    if (!editing) return;
    if (!/^\d+$/.test(editing.value.trim())) { toast.error('Starting number must be a whole number'); return; }
    try {
      await mutate(`document-series/${editing.id}`, 'PATCH', { entityId, startNumber: Number(editing.value) });
      setEditing(null); toast.success('Starting number updated'); reload();
    } catch (e) { toast.error((e as Error).message); }
  };

  const natureLabel = (code: number) => natures.find((n) => n.code === code)?.label ?? `Category ${code}`;
  const { askDelete, deleteDialog } = useDeleteConfirm();

  const summary = loading ? 'loading…'
    : error ? 'could not load'
    : items.length === 0 ? '— none on file'
    : `· ${items.length} ${items.length === 1 ? 'series' : 'series'}`;

  return (
    <Card
      collapsible
      defaultOpen={false}
      summary={summary}
      title="Document series"
      subtitle="GSTR-1 Table 13 summarises every series issued in the period — including cancelled documents and series that produced no filed invoice. Mandatory since May 2025."
      action={!loading && !error
        ? <AddToggle open={adding} onToggle={() => setAdding(!adding)} label="Add series" />
        : undefined}
    >
      {loading && <Loading label="Loading document series" />}
      {error && <div className="px-6 py-5"><ErrBox msg={error} /></div>}

      {!loading && !error && <>
      {adding && (
        <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
          <FormError msg={err} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Series name *"><TextInput value={f.name} onChange={set('name')} placeholder="Sales — Bangalore" /></Field>
            <Field label="Starting number *" hint="The number the series begins with. New documents made in Oddits continue from here.">
              <TextInput value={f.startNumber} onChange={set('startNumber')} placeholder="1" mono />
            </Field>
            <Field label="Prefix" hint="Optional. How the numbers start, e.g. INV/">
              <TextInput value={f.prefix} onChange={set('prefix')} placeholder="INV/" mono />
            </Field>
            <Field label="Suffix" hint="Optional. How the numbers end, e.g. /26-27. Use /{FY} for a year that changes each April.">
              <TextInput value={f.suffix} onChange={set('suffix')} placeholder="/{FY}" mono />
            </Field>
            <Field label="Number width" hint="Optional. Zero-padding: 3 gives 001.">
              <TextInput value={f.numberWidth} onChange={set('numberWidth')} placeholder="none" mono />
            </Field>
            <Field label="GSTN category" hint="Fixed category codes 1-12 that Table 13 reports against.">
              <Select
                value={f.natureCode}
                onChange={set('natureCode')}
                options={natures.map((n) => ({ value: String(n.code), label: `${n.code} — ${n.label}` }))}
                placeholder="Select category"
              />
            </Field>
            <Field label="Tally voucher type" hint="Which voucher type this series belongs to.">
              <TextInput value={f.voucherType} onChange={set('voucherType')} placeholder="Sales" />
            </Field>
          </div>
          <label className="mt-4 flex w-fit cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={f.restartEveryFy} onChange={(e) => setF((s) => ({ ...s, restartEveryFy: e.target.checked }))} />
            Restart from the starting number every financial year (1 April)
          </label>
          {/* On by default, except for purchases: a purchase bill is known by the
              supplier's invoice number, so numbering our side of it has to be
              asked for. The supplier's number is never changed either way. */}
          <label className="mt-2 flex w-fit cursor-pointer items-start gap-2 text-sm text-ink-700">
            <input type="checkbox" className="mt-1"
              checked={f.autoNumber ?? !/purchase/i.test(f.voucherType)}
              onChange={(e) => setF((s) => ({ ...s, autoNumber: e.target.checked }))} />
            <span>
              Number new documents made in Oddits from this series
              {/purchase/i.test(f.voucherType) && (
                <span className="block text-xs text-ink-500">Off by default for purchases. The supplier&rsquo;s invoice number is kept as it is whether this is on or off.</span>
              )}
            </span>
          </label>
          {/^\d+$/.test(f.startNumber.trim()) && (
            <p className="mt-3 text-xs text-ink-500">
              First number:{' '}
              <span className="font-mono text-ink-700">
                {f.prefix.split('{FY}').join(fy ? fy.label.slice(2) : '26-27')}
                {f.startNumber.trim().padStart(Number(f.numberWidth) > 0 ? Number(f.numberWidth) : 0, '0')}
                {f.suffix.split('{FY}').join(fy ? fy.label.slice(2) : '26-27')}
              </span>
              {f.suffix.includes('{FY}') || f.prefix.includes('{FY}') ? ' — {FY} is the financial year the document is dated in.' : ''}
            </p>
          )}
          {/* What the books say about this series, before it is saved. */}
          {check?.warning && (
            <div className="mt-3 rounded-xl border border-warn-200 bg-warn-50 px-4 py-3 text-xs text-warn-800">
              <p className="font-medium">{check.warning}</p>
              {check.preview && <p className="mt-1">This series would write <span className="font-mono">{check.preview}</span>.</p>}
              <label className="mt-2 flex w-fit cursor-pointer items-center gap-2">
                <input type="checkbox" checked={f.autoNumber === true} onChange={(e) => setF((s) => ({ ...s, autoNumber: e.target.checked ? true : null }))} />
                This is a new series — number new documents from it anyway
              </label>
            </div>
          )}
          {check?.overlapWarning && <p className="mt-2 text-xs text-warn-700">{check.overlapWarning}</p>}
          {check?.startHint && <p className="mt-2 text-xs text-warn-700">{check.startHint}</p>}
          {check && !check.warning && check.status === 'ok' && (
            <p className="mt-2 text-xs text-ok-700">Matches the numbers in the books{check.next ? <> — next would be <span className="font-mono">{check.next}</span></> : null}.</p>
          )}
          <div className="mt-5 flex items-center gap-3">
            <PrimaryBtn onClick={submit} busy={busy}>Add series</PrimaryBtn>
            <button onClick={() => setAdding(false)} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
          </div>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="border-b border-ink-100 px-6 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">From the document numbers in the books</p>
          <ul className="mt-2 space-y-1.5">
            {suggestions.slice(0, 10).map((s) => (
              <li key={`${s.voucherType}|${s.prefix}|${s.suffix}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className="text-ink-700">{s.voucherType}</span>
                <span className="font-mono text-xs text-ink-800">{s.prefix}<span className="text-ink-400">#</span>{s.suffix}</span>
                <span className="text-xs text-ink-400">
                  {fy && s.inPeriod ? `${s.inPeriod} in FY ${fy.label}` : `${s.count} documents${fy ? `, none in FY ${fy.label}` : ''}`}, e.g. {s.example}
                </span>
                <button onClick={() => fillFrom(s)} className="rounded-lg border border-ink-300 bg-paper px-2 py-0.5 text-[11px] font-semibold text-ink-700 hover:bg-ink-50">Use</button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {items.length === 0 && !adding ? (
        <EmptyState
          what="document series"
          why="Table 13 cannot be built from filed invoices alone — it must report cancelled documents and unused series too, which only the series definition provides."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr><Th>Series</Th><Th>Numbers</Th><Th>Starts at</Th><Th>{fy ? `FY ${fy.label}` : 'This year'}</Th><Th>GSTN category</Th><Th>Voucher type</Th><Th right>Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {items.map((s) => (
                <tr key={s.id} className="hover:bg-ink-50/50">
                  <Td>
                    <span className="inline-flex items-center gap-2">
                      <FileStack className="h-3.5 w-3.5 text-ink-400" />{s.name}
                      {!s.active && <Pill tone="neutral">inactive</Pill>}
                    </span>
                  </Td>
                  <Td mono>{s.prefix ?? ''}<span className="text-ink-400">{'#'.repeat(Math.max(1, s.numberWidth ?? 1))}</span>{s.suffix ?? ''}</Td>
                  <Td mono>
                    {editing?.id === s.id ? (
                      <span className="inline-flex items-center gap-1">
                        <input
                          autoFocus value={editing.value}
                          onChange={(e) => setEditing({ id: s.id, value: e.target.value })}
                          onKeyDown={(e) => { if (e.key === 'Enter') saveStart(); if (e.key === 'Escape') setEditing(null); }}
                          className="w-20 rounded-lg border border-ink-300 px-2 py-1 font-mono text-xs focus:border-inkblack focus:outline-none"
                          aria-label="Starting number"
                        />
                        <button onClick={saveStart} className="rounded-lg bg-inkblack px-2 py-1 font-sans text-[11px] font-semibold text-white">Save</button>
                      </span>
                    ) : (
                      <button onClick={() => setEditing({ id: s.id, value: String(s.startNumber ?? 1) })} title="Change the starting number" className="rounded px-1 underline decoration-dotted underline-offset-2 hover:bg-ink-100">
                        {s.startNumber ?? 1}
                      </button>
                    )}
                    <span className="block font-sans text-[11px] text-ink-400">{s.restartEveryFy ? 'restarts each April' : 'runs on'}</span>
                    {s.autoNumbers === false && <span className="block font-sans text-[11px] text-ink-400">not auto-numbered</span>}
                  </Td>
                  <Td>
                    {s.usage ? (
                      <span className="text-xs">
                        {s.usage.count > 0
                          ? <span className="font-mono text-ink-700">{s.usage.first} → {s.usage.last} <span className="font-sans text-ink-400">({s.usage.count})</span></span>
                          : <span className="text-ink-400">none yet</span>}
                        {/* A next number only where the series can read the
                            books' numbers. "SIH1" beside "SIH/166/26-27" is
                            not a next number, it is a wrong one. */}
                        {s.usage.next
                          ? <span className="block text-[11px] text-ink-500">next: <span className="font-mono">{s.usage.next}</span></span>
                          : <span className="block text-[11px] text-ink-400">no next number</span>}
                        {s.usage.warning && <span className="mt-0.5 block text-[11px] text-warn-700">⚠ {s.usage.warning}</span>}
                        {s.usage.overlapWarning && <span className="mt-0.5 block text-[11px] text-warn-700">⚠ {s.usage.overlapWarning}</span>}
                        {s.usage.startHint && <span className="mt-0.5 block text-[11px] text-warn-700">{s.usage.startHint}</span>}
                        {/* Two systems numbering from one series cannot see each
                            other's last number until the next sync. */}
                        {s.usage.mixed && (
                          <span className="mt-0.5 block text-[11px] text-warn-700" title="Tally and Oddits each issue the next number without seeing the other's until a sync. Number this series in one place.">
                            ⚠ used by Tally ({s.usage.fromTally}) and Oddits ({s.usage.fromOddits}) — risk of duplicate numbers
                          </span>
                        )}
                      </span>
                    ) : '—'}
                  </Td>
                  <Td muted>{s.natureCode} — {natureLabel(s.natureCode)}</Td>
                  <Td muted>{s.voucherType ?? '—'}</Td>
                  <Td right>
                    <GhostBtn tone="danger" onClick={() => askDelete(`series ${s.name}`, async () => {
                      try { await mutate(`document-series/${s.id}`, 'DELETE', { entityId }); toast.success('Series removed'); reload(); }
                      catch (e) { toast.error((e as Error).message); }
                    })}><Trash2 className="h-3 w-3" /></GhostBtn>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      </>}
      {deleteDialog}
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Stock items
   ══════════════════════════════════════════════════════════════════════ */

interface StockItem {
  id: string; name: string; parent: string | null; hsnCode: string | null; hsnType: string | null;
  uqc: string | null; unitName: string | null; gstRate: string | null; cessRate: string | null;
  source: string; edited: boolean;
}

interface ServiceLedger {
  id: string; name: string; group: string | null; sacCode: string | null;
  gstRate: number | null; description: string | null; typeOfSupply: string | null;
}
interface NeedsCode { id: string; name: string; group: string | null }

/**
 * One item row, with its codes editable in place.
 *
 * Tally-sourced items used to be read-only here, so an item whose HSN Tally
 * never carried could only be corrected in the client's own books — and the
 * client is exactly who could not be relied on to do it. Dwarakamai's October
 * was refused by GSTN (RET191350) for a four-digit rice code against a ₹12
 * crore turnover, and there was nowhere in oddits for the professional team to
 * put the right one.
 *
 * Editing does NOT write to Tally. It records the firm's own answer, which
 * later pulls keep (`edited`), and which outranks the voucher's own code when
 * the return is built — see the hsn resolution in returns.service.ts. Without
 * that precedence this screen would accept a correction and change nothing.
 */
function ItemRow({ item, entityId, uqcCodes, onSaved, onDelete }: {
  item: StockItem; entityId: string | null; uqcCodes: string[];
  onSaved: () => void; onDelete: () => void;
}) {
  const [edit, setEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    hsnCode: item.hsnCode ?? '', uqc: item.uqc ?? '',
    gstRate: item.gstRate != null ? String(Number(item.gstRate)) : '',
    cessRate: item.cessRate != null ? String(Number(item.cessRate)) : '',
  });
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const open = () => {
    setF({
      hsnCode: item.hsnCode ?? '', uqc: item.uqc ?? '',
      gstRate: item.gstRate != null ? String(Number(item.gstRate)) : '',
      cessRate: item.cessRate != null ? String(Number(item.cessRate)) : '',
    });
    setEdit(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      /* Sent whether or not it changed: the API treats an absent key as "leave
         it", so omitting a cleared field would silently keep the old value. */
      await mutate(`stock-items/${item.id}`, 'PATCH', {
        entityId, hsnCode: f.hsnCode.trim(), uqc: f.uqc,
        gstRate: f.gstRate.trim(), cessRate: f.cessRate.trim(),
      });
      toast.success(`${item.name} updated`);
      setEdit(false); onSaved();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  if (edit) return (
    <tr className="bg-brand-50/40">
      <Td>
        <span className="inline-flex items-center gap-2"><Package className="h-3.5 w-3.5 text-ink-400" />{item.name}</span>
      </Td>
      <Td muted>{item.parent ?? '—'}</Td>
      <Td><TextInput value={f.hsnCode} onChange={set('hsnCode')} placeholder="100630" mono /></Td>
      <Td><Select value={f.uqc} onChange={set('uqc')} options={uqcCodes} placeholder="Unit" /></Td>
      <Td><TextInput value={f.gstRate} onChange={set('gstRate')} placeholder="0" mono /></Td>
      <Td><TextInput value={f.cessRate} onChange={set('cessRate')} placeholder="0" mono /></Td>
      <Td muted>—</Td>
      <Td right>
        <span className="inline-flex items-center gap-2">
          <PrimaryBtn onClick={save} busy={busy}><Check className="h-3 w-3" /> Save</PrimaryBtn>
          <button onClick={() => setEdit(false)} className="text-xs font-medium text-ink-500 hover:text-ink-700">Cancel</button>
        </span>
      </Td>
    </tr>
  );

  return (
    <tr className="hover:bg-ink-50/50">
      <Td>
        <span className="inline-flex items-center gap-2"><Package className="h-3.5 w-3.5 text-ink-400" />{item.name}</span>
      </Td>
      <Td muted>{item.parent ?? '—'}</Td>
      <Td mono>
        {item.hsnCode
          ? <>{item.hsnCode}{item.hsnType && <span className="ml-1 text-ink-400">{item.hsnType}</span>}</>
          : <button onClick={open} className="font-mono text-xs text-danger-600 underline decoration-dotted hover:text-danger-700">missing — set it</button>}
      </Td>
      {/* A service has no unit: GSTN files it as NA, and that is not a gap.
          Goods whose unit has no GSTN code file as OTH, and say so. */}
      <Td mono>{item.uqc === 'NA'
        ? <span title="A service (SAC) has no unit. Filed as NA with quantity 0.">NA <span className="font-sans text-ink-400">(service)</span></span>
        : item.uqc ?? <span className="text-warn-700" title="This item's unit has no GSTN code. It is filed as OTH in Table 12.">OTH — unit not mapped</span>}</Td>
      <Td right mono>{item.gstRate != null ? `${Number(item.gstRate)}%` : '—'}</Td>
      <Td right mono>{item.cessRate != null ? `${Number(item.cessRate)}%` : '—'}</Td>
      <Td><Pill tone={item.source === 'tally' ? 'neutral' : 'brand'}>{item.source}{item.edited ? ' · edited' : ''}</Pill></Td>
      <Td right>
        <span className="inline-flex items-center gap-1">
          <GhostBtn onClick={open}><Pencil className="h-3 w-3" /></GhostBtn>
          {item.source === 'manual' && <GhostBtn tone="danger" onClick={onDelete}><Trash2 className="h-3 w-3" /></GhostBtn>}
        </span>
      </Td>
    </tr>
  );
}

export function ItemsTab({ entityId }: { entityId: string | null }) {
  const { items, loading, error, reload } = useEntityResource<StockItem>('stock-items', entityId);
  const [svc, setSvc] = useState<{ services: ServiceLedger[]; needsCode: NeedsCode[] }>({ services: [], needsCode: [] });
  const { askDelete, deleteDialog } = useDeleteConfirm();
  const [uqcCodes, setUqcCodes] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', parent: '', hsnCode: '', uqc: '', unitName: '', gstRate: '', cessRate: '' });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (v: string) => setF((s) => ({ ...s, [k]: v }));

  const loadServices = () => {
    if (!entityId) return;
    adminFetchJson<{ services: ServiceLedger[]; needsCode: NeedsCode[] }>(
      '/api/masters/items-services', { query: { entityId } },
    ).then((d) => setSvc({ services: d.services ?? [], needsCode: d.needsCode ?? [] })).catch(() => {});
  };
  useEffect(loadServices, [entityId]);

  useEffect(() => {
    adminFetchJson<{ uqcCodes: string[] }>('/api/masters/meta')
      .then((d) => setUqcCodes(d.uqcCodes ?? []))
      .catch(() => setUqcCodes([]));
  }, []);

  if (loading) return <Loading label="Loading items" />;
  if (error) return <ErrBox msg={error} />;

  const noCode = items.filter((i) => !i.hsnCode);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await mutate('stock-items', 'POST', { entityId, ...f });
      setAdding(false); setF({ name: '', parent: '', hsnCode: '', uqc: '', unitName: '', gstRate: '', cessRate: '' });
      toast.success('Item added'); reload();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <>
    <Card
      title="Goods"
      subtitle="Stock items from Tally's inventory. GSTR-1 Table 12 reports quantity and unit per HSN, which is why UQC matters here."
      action={<AddToggle open={adding} onToggle={() => setAdding(!adding)} label="Add item" />}
    >
      {adding && (
        <div className="border-b border-ink-100 bg-ink-50/60 px-6 py-5">
          <FormError msg={err} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Item name"><TextInput value={f.name} onChange={set('name')} /></Field>
            <Field label="Stock group"><TextInput value={f.parent} onChange={set('parent')} /></Field>
            <Field label="HSN / SAC" hint="4, 6 or 8 digits. Codes starting 99 are services (SAC).">
              <TextInput value={f.hsnCode} onChange={set('hsnCode')} placeholder="8471" mono />
            </Field>
            <Field label="UQC" hint="Mandatory in Table 12. Use OTH where a quantity is not meaningful.">
              <Select value={f.uqc} onChange={set('uqc')} options={uqcCodes} placeholder="Select unit" />
            </Field>
            <Field label="Tally unit name" hint="The client's own unit, before mapping to a UQC.">
              <TextInput value={f.unitName} onChange={set('unitName')} />
            </Field>
            <Field label="GST rate %"><TextInput value={f.gstRate} onChange={set('gstRate')} placeholder="18" mono /></Field>
            <Field label="Cess rate %"><TextInput value={f.cessRate} onChange={set('cessRate')} placeholder="0" mono /></Field>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <PrimaryBtn onClick={submit} busy={busy}>Add item</PrimaryBtn>
            <button onClick={() => setAdding(false)} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
          </div>
        </div>
      )}

      {/* Named before it is discovered on a rejection: GSTN refuses a Table 12
          row with a blank HSN, and refuses the WHOLE return for one code that
          is short for the turnover. Both are typed here, once. */}
      {noCode.length > 0 && (
        <div className="border-b border-ink-100 bg-danger-50/60 px-6 py-3 text-xs text-danger-800">
          <span className="font-semibold">{noCode.length} item{noCode.length > 1 ? 's have' : ' has'} no HSN</span>
          {' \u2014 '}{noCode.map((i) => i.name).join(', ')}. GSTN rejects a Table 12 row with a blank code.
          Set each one here; above \u20b95 crore turnover it must be 6 or 8 digits.
        </div>
      )}
      {items.length === 0 && !adding ? (
        <EmptyState
          what="stock item"
          why="Normal for a services client — Tally only carries stock items where the client actually maintains inventory. Goods traders need them so Table 12 can report quantities."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr><Th>Item</Th><Th>Group</Th><Th>HSN/SAC</Th><Th>UQC</Th><Th right>GST %</Th><Th right>Cess %</Th><Th>Source</Th><Th right>Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {items.map((i) => (
                <ItemRow
                  key={i.id} item={i} entityId={entityId} uqcCodes={uqcCodes}
                  onSaved={reload}
                  onDelete={() => askDelete(`item ${i.name}`, async () => {
                    try { await mutate(`stock-items/${i.id}`, 'DELETE', { entityId }); toast.success('Item removed'); reload(); }
                    catch (e) { toast.error((e as Error).message); }
                  })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {deleteDialog}
    </Card>

    <ServicesCard services={svc.services} needsCode={svc.needsCode} entityId={entityId} onChanged={loadServices} />
    <HsnMasterCard />
    </>
  );
}

/**
 * Services are ledgers carrying a SAC, not a master of their own.
 *
 * In Tally a service is not a stock item — it is a sales ledger with a chapter
 * 99 code on it, and that already arrives with every sync (38 such ledgers
 * existed in production before this screen did). A separate services master
 * would duplicate the SAC that lives on the ledger and drift from it, so this
 * reads the ledgers rather than copying them.
 */
function ServicesCard({ services, needsCode, entityId, onChanged }: {
  services: ServiceLedger[]; needsCode: NeedsCode[]; entityId: string | null; onChanged: () => void;
}) {
  const [assigning, setAssigning] = useState<null | { id?: string; name?: string }>(null);

  return (
    <Card
      title="Services"
      subtitle="Sales ledgers carrying a SAC code (chapter 99). These are ledgers, not a separate master — change one in Chart of accounts and it changes here."
      action={
        <button
          onClick={() => setAssigning({})}
          className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-3.5 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add service
        </button>
      }
    >
      {services.length === 0 ? (
        <EmptyState
          what="service"
          why="No sales ledger carries a SAC code yet. A goods-only client has none; otherwise set the SAC on the sales ledger in Tally, or in Chart of accounts."
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-100 bg-ink-50/60 text-left text-xs uppercase tracking-wide text-ink-500">
              <tr><Th>Service ledger</Th><Th>Group</Th><Th>SAC</Th><Th>Description</Th><Th right>GST %</Th></tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {services.map((s) => (
                <tr key={s.id} className="hover:bg-ink-50/50">
                  <Td>
                    <button onClick={() => setAssigning({ id: s.id, name: s.name })} className="text-left text-ink-800 hover:underline">
                      {s.name}
                    </button>
                  </Td>
                  <Td muted>{s.group ?? '—'}</Td>
                  <Td mono>{s.sacCode}</Td>
                  <Td muted>
                    {s.description ?? <span className="text-warn-700">not in the HSN/SAC master</span>}
                  </Td>
                  <Td right mono>{s.gstRate != null ? `${s.gstRate}%` : '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {needsCode.length > 0 && (
        <div className="border-t border-ink-100 px-6 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Income ledgers with no HSN or SAC — {needsCode.length}
          </p>
          {/* Not asserted as a defect: interest and profit on sale are income
              but not supplies, so they need no code at all. */}
          <p className="mt-1 max-w-2xl text-xs text-ink-600">
            Every outward <em>supply</em> needs one for Table 12. Some of these
            legitimately do not — interest and profit on sale are income but not
            supplies — so this is a list to check, not a list to fill.
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {needsCode.map((n) => (
              <button
                key={n.id}
                onClick={() => setAssigning({ id: n.id, name: n.name })}
                title="Set a SAC code on this ledger"
                className="inline-flex items-center gap-1.5 rounded-full border border-ink-200 bg-paper px-2.5 py-1 text-xs text-ink-700 transition hover:border-inkblack hover:bg-ink-50"
              >
                {n.name}
                {n.group && <span className="text-ink-400">{n.group}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {assigning && (
        <AssignSacDialog
          entityId={entityId}
          preset={assigning}
          onClose={() => setAssigning(null)}
          onSaved={() => { setAssigning(null); onChanged(); }}
        />
      )}
    </Card>
  );
}

/**
 * The HSN/SAC master, managed from the app.
 *
 * The table is firm-wide (the nomenclature is statutory, identical for every
 * client) and until now could only be filled by someone with shell access via
 * scripts/load-hsn-master.ts — which is why production sat at zero codes while
 * the Gaps tab reported GSTR-1 as blocked. Table 12 populates its description
 * from this master and rejects free text, so an empty master is a hard stop.
 */
function HsnMasterCard() {
  const [stats, setStats] = useState<{ total: number; hsn: number; sac: number; starter: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<{ loaded: number; replaced: number; kept: number; skipped: { row: number; code: string; reason: string }[] } | null>(null);

  const load = () => adminFetchJson<{ total: number; hsn: number; sac: number; starter: number }>('/api/gst/hsn/stats')
    .then(setStats).catch(() => {});
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy(true); setReport(null);
    try {
      const r = await adminFetchJson<any>('/api/gst/hsn/seed-starter', { method: 'POST' });
      setReport(r); toast.success(`${r.loaded} code(s) added`); load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  const upload = async (file: File) => {
    setBusy(true); setReport(null);
    try {
      const text = await file.text();
      const r = await adminFetchJson<any>('/api/gst/hsn/import', {
        method: 'POST', headers: { 'Content-Type': 'text/csv' }, body: text,
      });
      setReport(r);
      toast.success(`${r.loaded} code(s) loaded${r.skipped?.length ? `, ${r.skipped.length} skipped` : ''}`);
      load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusy(false); }
  };

  const empty = (stats?.total ?? 0) === 0;

  return (
    <Card
      collapsible
      defaultOpen={empty}
      summary={stats ? (empty ? '— empty, Table 12 is blocked' : `· ${stats.total} codes`) : 'loading…'}
      title="HSN / SAC master"
      subtitle="Statutory nomenclature, shared across every client. GSTR-1 Table 12 fills its description from here and rejects free text."
    >
      <div className="px-6 py-5">
        {stats && (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
            <Pill tone={empty ? 'danger' : 'ok'}>{stats.total} codes</Pill>
            <Pill tone="neutral">{stats.hsn} HSN</Pill>
            <Pill tone="neutral">{stats.sac} SAC</Pill>
            {stats.starter > 0 && <Pill tone="warn">{stats.starter} from the starter set</Pill>}
          </div>
        )}

        {stats && stats.starter > 0 && (
          <p className="mb-4 max-w-2xl text-xs text-warn-700">
            The starter set is a small hand-checked list, not GSTN's official master.
            Load the real dataset when you have it — it replaces these, and codes you
            import are never overwritten by seeding again.
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-paper transition hover:bg-inkblack-80 ${busy ? 'pointer-events-none opacity-50' : ''}`}>
            <Upload className="h-3.5 w-3.5" /> Load a CSV
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }}
            />
          </label>
          <button
            onClick={seed}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-4 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50"
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Add the starter set
          </button>
        </div>

        <p className="mt-3 max-w-2xl text-xs text-ink-500">
          The CSV needs a code column and a description column; type, rate and
          effective-from are optional and column naming is flexible. Codes must be
          4, 6 or 8 digits — anything else is reported back and skipped rather
          than guessed at. Rates are not used for filing: the rate on a return is
          the one actually charged on the transaction.
        </p>

        {report && (
          <div className="mt-4 rounded-xl border border-ink-200 bg-ink-50/60 px-4 py-3 text-xs text-ink-700">
            <p className="font-semibold">
              {report.loaded} loaded
              {report.replaced ? `, ${report.replaced} updated` : ''}
              {report.kept ? `, ${report.kept} already present and left alone` : ''}
              {report.skipped?.length ? `, ${report.skipped.length} skipped` : ''}
            </p>
            {report.skipped?.slice(0, 8).map((s) => (
              <p key={`${s.row}-${s.code}`} className="mt-1 text-warn-700">
                row {s.row} “{s.code || '—'}” — {s.reason}
              </p>
            ))}
            {report.skipped?.length > 8 && <p className="mt-1 text-ink-500">…and {report.skipped.length - 8} more</p>}
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Put a SAC code on a ledger — the real meaning of "add a service".
 *
 * Goods and services are asymmetric here and that is not an oversight: a stock
 * item is a record the app creates, whereas a service already exists as a sales
 * ledger the moment Tally syncs. What is missing is never the ledger, it is the
 * chapter-99 code on it. So this assigns rather than creates, and the SAC comes
 * from the HSN/SAC master rather than a free-text box — Table 12 fills its
 * description from that master and rejects anything not in it.
 */
function AssignSacDialog({ entityId, preset, onClose, onSaved }: {
  entityId: string | null;
  preset: { id?: string; name?: string };
  onClose: () => void;
  onSaved: () => void;
}) {
  const [ledgers, setLedgers] = useState<{ id: string; name: string; parent: string | null }[]>([]);
  const [ledgerId, setLedgerId] = useState(preset.id ?? '');
  const [ledgerQ, setLedgerQ] = useState('');
  const [sacQ, setSacQ] = useState('');
  const [sacs, setSacs] = useState<{ code: string; description: string }[]>([]);
  const [sac, setSac] = useState('');
  const [rate, setRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!entityId || preset.id) return; // a preset ledger needs no picker
    adminFetchJson<{ items: { id: string; name: string; parent: string | null }[] }>(
      '/api/tally-import/ledger-names', { query: { entityId } },
    ).then((d) => setLedgers(d.items ?? [])).catch(() => {});
  }, [entityId, preset.id]);

  // Only SACs: assigning an 8471 to something called "Consulting" is the error
  // this screen exists to prevent.
  useEffect(() => {
    const t = setTimeout(() => {
      adminFetchJson<{ items: { code: string; description: string }[] }>(
        '/api/gst/hsn', { query: { type: 'SAC', q: sacQ } },
      ).then((d) => setSacs(d.items ?? [])).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [sacQ]);

  const ledgerMatches = ledgerQ.trim()
    ? ledgers.filter((l) => l.name.toLowerCase().includes(ledgerQ.trim().toLowerCase())).slice(0, 30)
    : ledgers.slice(0, 30);

  const save = async () => {
    if (!ledgerId) { setErr('Pick the ledger this service is billed through.'); return; }
    if (!sac) { setErr('Pick a SAC code.'); return; }
    setBusy(true); setErr(null);
    try {
      await adminFetchJson(`/api/gst/ledger/${ledgerId}`, {
        method: 'PATCH',
        query: { entityId: entityId ?? undefined },
        body: JSON.stringify({ hsnCode: sac, ...(rate.trim() ? { gstRate: Number(rate) } : {}) }),
      });
      toast.success('SAC set — it now appears under Services');
      onSaved();
    } catch (e) {
      setErr((e as Error).message?.replace(/^\d+\s/, '') || 'Could not save');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl border border-ink-200 bg-paper shadow-xl">
        <div className="flex shrink-0 items-center justify-between border-b border-ink-100 px-6 py-4">
          <h3 className="text-lg font-semibold text-ink-900">Add a service</h3>
          <GhostBtn onClick={onClose}><X className="h-4 w-4" /></GhostBtn>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          <FormError msg={err} />

          <p className="mb-4 text-xs text-ink-600">
            A service is a sales ledger with a chapter-99 SAC on it, so this puts
            the code on a ledger you already have rather than creating a second
            record. To bill something genuinely new, create the ledger in{' '}
            <span className="font-medium text-ink-800">Books → Ledgers</span> first.
          </p>

          <Field label="Ledger">
            {preset.id ? (
              <div className="rounded-xl border border-ink-200 bg-ink-50/60 px-4 py-2.5 text-sm text-ink-800">{preset.name}</div>
            ) : (
              <>
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                  <input
                    value={ledgerQ}
                    onChange={(e) => setLedgerQ(e.target.value)}
                    placeholder="Search ledgers…"
                    className="w-full rounded-xl border border-ink-200 bg-paper py-2.5 pl-9 pr-3 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
                  />
                </div>
                <div className="mt-2 max-h-40 overflow-y-auto rounded-xl border border-ink-100">
                  {ledgerMatches.map((l) => (
                    <button
                      key={l.id}
                      onClick={() => setLedgerId(l.id)}
                      className={`flex w-full items-center justify-between px-3 py-1.5 text-left text-sm ${ledgerId === l.id ? 'bg-highlighter-40' : 'hover:bg-ink-50'}`}
                    >
                      <span className="truncate text-ink-800">{l.name}</span>
                      {l.parent && <span className="ml-2 shrink-0 text-[11px] text-ink-400">{l.parent}</span>}
                    </button>
                  ))}
                  {ledgerMatches.length === 0 && <p className="px-3 py-2 text-xs text-ink-500">No match.</p>}
                </div>
              </>
            )}
          </Field>

          <div className="mt-4">
            <Field label="SAC code" hint="From the HSN/SAC master. Table 12 fills its description from here and rejects free text.">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <input
                  value={sacQ}
                  onChange={(e) => setSacQ(e.target.value)}
                  placeholder="Search by code or description…"
                  className="w-full rounded-xl border border-ink-200 bg-paper py-2.5 pl-9 pr-3 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
                />
              </div>
            </Field>
            <div className="mt-2 max-h-44 overflow-y-auto rounded-xl border border-ink-100">
              {sacs.map((c) => (
                <button
                  key={c.code}
                  onClick={() => setSac(c.code)}
                  className={`flex w-full items-start gap-3 px-3 py-1.5 text-left text-sm ${sac === c.code ? 'bg-highlighter-40' : 'hover:bg-ink-50'}`}
                >
                  <span className="shrink-0 font-mono text-xs text-ink-600">{c.code}</span>
                  <span className="truncate text-ink-800">{c.description}</span>
                </button>
              ))}
              {sacs.length === 0 && (
                <p className="px-3 py-2 text-xs text-ink-500">
                  No SAC matches. If the master is empty or missing this code, load the
                  official list from the HSN / SAC master card below.
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <Field label="GST rate %" hint="Optional. Leave blank if it varies by transaction.">
              <TextInput value={rate} onChange={setRate} placeholder="18" mono />
            </Field>
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-ink-100 px-6 py-4">
          <GhostBtn onClick={onClose}>Cancel</GhostBtn>
          <PrimaryBtn onClick={save} busy={busy}>Set SAC</PrimaryBtn>
        </div>
      </div>
    </div>
  );
}
